'use client';

import {useEffect, useRef} from 'react';
import {motion} from 'framer-motion';
import type {Recommendation as RecommendationType} from '@manaboo/recommendation';
import {CardGame} from '@/components/card-game';
import {insertRecommendationImpressions} from '@/actions/insert-recommendation-impressions';
import {cn} from '@manaboo/ui/cn';

interface RecommendationProps {
	recommendation: RecommendationType;
}

const containerVariants = {
	hidden: {opacity: 0},
	show: {
		opacity: 1,
		transition: {
			staggerChildren: 0.05,
		},
	},
};

const itemVariants = {
	hidden: {opacity: 0, y: 20},
	show: {
		opacity: 1,
		y: 0,
		transition: {
			duration: 0.5,
		},
	},
};

export function Recommendation({recommendation}: RecommendationProps) {
	const sectionRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!sectionRef.current || recommendation.games.length === 0) {
			return;
		}

		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach(async (entry) => {
					if (entry.isIntersecting) {
						const impressions = recommendation.games.map((game, index) => ({
							game_id: game.id,
							campaign_id: game?.campaign?.campaign_id ?? null,
							recommendation_version_id: recommendation.version_id,
							recommendation_section_id: recommendation.id,
							position: index + 1,
						}));

						await insertRecommendationImpressions(impressions);
						observer.disconnect();
					}
				});
			},
			{
				threshold: 0.5,
				rootMargin: '50px',
			},
		);

		observer.observe(sectionRef.current);

		return () => {
			observer.disconnect();
		};
	}, [recommendation]);

	if (recommendation.games.length === 0) {
		return null;
	}

	return (
		<div
			ref={sectionRef}
			className="mb-8 flex flex-col gap-5"
		>
			<motion.h2
				initial={{opacity: 0}}
				animate={{opacity: 1}}
				className="text-xl"
			>
				{recommendation.title}:
			</motion.h2>

			{recommendation.description ? (
					<motion.p
						initial={{opacity: 0}}
						animate={{opacity: 1}}
					>
						{recommendation.description}
					</motion.p>
			) : null}

			<motion.div
				variants={containerVariants}
				initial="hidden"
				whileInView="show"
				viewport={{once: true, margin: '-100px'}}
				className="grid grid-cols-2 gap-x-1 md:gap-x-2 gap-y-1 lg:grid-cols-3 xl:grid-cols-5"
			>
				{recommendation.games.map((game, index) => (
					<motion.div
						key={game.id}
						variants={itemVariants}
					>
						<CardGame
							game={game}
							recommendation={{
								version_id: recommendation.version_id,
								section_id: recommendation.id,
								position: index + 1,
							}}
							// Ranking recommendation
							index={recommendation.id === 7 ? index : undefined}
						/>
					</motion.div>
				))}
			</motion.div>
		</div>
	);
}
